/**
 * CombatAnimationTiming.ts — Shared combat animation timing constants.
 *
 * Single source of truth for the per-event animation delays used by:
 *  - The frontend (CombatAnimationView), which schedules local animation
 *    timing for the batched CombatStream.
 *  - The backend (DevServer), which delays the post-combat state broadcast
 *    (next round / game over) until the frontend has finished animating the
 *    stream.
 *
 * Keeping the delays in one shared place guarantees the server never sends
 * the next round's state before the client has finished playing the combat
 * animation — otherwise the combat view would be reset mid-animation and
 * combats would appear to end early.
 */

import type { CombatStreamEvent } from './messages/MessageContracts';

/**
 * Per-event animation delays in milliseconds, keyed by stream event
 * `messageType`. These mirror the old server-side staggered streaming
 * delays so animations keep their original pacing.
 */
export const COMBAT_EVENT_ANIMATION_DELAYS: Record<string, number> = {
  // Attack (task 220): 300 → 450 — the lunge takes ~224ms (0.7 × 320) and
  // the old in-flight guard silently DROPPED a second attack arriving
  // while the first lunge was still in flight (now fixed: the lunges run
  // on a promise chain in main.ts and are never skipped — the queue test
  // is tests/attackAnimationQueue.test.ts). 450ms is kept as the pacing
  // between attacks. (The table is shared — the server's post-combat
  // wait grows with it, so the longer playback is always covered.)
  CombatAttackEvent: 450,
  CombatDamageEvent: 180,
  CombatDeathEvent: 250,
  CombatKillGoldEvent: 150,
  CombatAbilityEvent: 180,
  // Exploding-shimmer: the red glow around the living Exploding source(s)
  // whose passive just fired for a death — it plays alongside the BOOM at
  // the corpse, so it is short (the CSS animation self-cleans in 0.9s).
  CombatExplodingShimmerEvent: 120,
  CombatAuraEvent: 100,
  CombatNecromancyEvent: 400,
  CombatSuddenDeathEvent: 500,
  // Toxic Cloud: the green cloud rolls over the whole enemy battlefield
  // (one trigger per enemy turn — the clouds stack, the FX is single), so
  // it gets a presence beat before its simultaneous damage events land.
  CombatToxicCloudEvent: 450,
  // Mark: the black-particle emitter starts at the target (the continuous
  // part is CSS-driven; this is just the moment-of-mark pacing).
  CombatMarkEvent: 180,
  // Shared Pain: the red expanding particle line(s) from the holder to the
  // marked victims need a visible beat before the damage events.
  CombatSharedPainEvent: 300,
  // Freeze: the large white ice/snow burst at the frozen target — it plays
  // right after the hit lands, so it gets the same beat as a Mark.
  CombatFreezeEvent: 180,
  // Freeze skip: the ice-shatter burst at the unfrozen (skipping) minion —
  // a presence beat in place of the attack that never happens.
  CombatFreezeSkipEvent: 300,
  // SwarmLord spawn: the board fills with Swarmers — every spawned card
  // plays its egg-hatching burst on this beat (one event carries the whole
  // batch), so it gets a presence beat like the Necromancy replacement.
  CombatSwarmEvent: 500,
  // Shield Breaker: the red shield-shatter burst on the targeted unit —
  // it plays on the same beat as the attack that broke it, so it gets the
  // attack's pacing (the stream order is attack → shieldBroken → damage).
  CombatShieldBrokenEvent: 450,
  CombatResult: 350,
};

/** Fallback delay (ms) for unknown stream event types. */
export const COMBAT_EVENT_DEFAULT_DELAY_MS = 300;

/**
 * Intro delay (ms) between the moment a combat's stream arrives at the
 * client and the moment the FIRST stream event plays. The client renders
 * the combat board from CombatStarted and needs a beat before the first
 * attack lands — on a slow page the opening attacks otherwise fire before
 * the board has rendered and are missed. Applied ONCE per combat — before
 * the first event only, never between events — and it is part of the
 * stream's total animation time (calculateCombatStreamDurationMs), so the
 * server's post-combat wait covers it too.
 */
export const COMBAT_STREAM_START_DELAY_MS = 3000;

/** How long the combat result overlay stays visible after the result event. */
export const COMBAT_RESULT_DISPLAY_MS = 1500;

/** Extra safety buffer (ms) appended after animation + result display. */
export const POST_COMBAT_BUFFER_MS = 250;

/**
 * Minimum remaining wait (ms) before a player is sent the "waiting for
 * other combats" RoundTransition message. When a player's stream is as
 * long as the longest one (or everyone watches the same single combat),
 * the only wait left after the result overlay auto-hides is
 * POST_COMBAT_BUFFER_MS (250 ms) — a sub-second flicker not worth
 * overlaying the board with. Only when the round advances clearly later
 * than that does the message help the player.
 */
export const MIN_ROUND_TRANSITION_WAIT_MS = 750;

/**
 * Minimum post-combat delay (ms). Preserves a ~1.5s wait for
 * degenerate cases (e.g., a combat with an empty stream).
 */
export const MIN_POST_COMBAT_DELAY_MS = 1500;

/** Returns the animation delay (ms) for a single stream event. */
export function getCombatEventDelayMs(event: { messageType: string }): number {
  return COMBAT_EVENT_ANIMATION_DELAYS[event.messageType] ?? COMBAT_EVENT_DEFAULT_DELAY_MS;
}

/**
 * Whether a stream event belongs to a "simultaneous" group — i.e., it is part
 * of a multi-target ability effect (e.g., Explosive) whose damage/deaths all
 * land at the same instant. Consecutive simultaneous events are animated at
 * the same time (see CombatAnimationView._processCombatStream).
 */
export function isSimultaneousStreamEvent(event: CombatStreamEvent): boolean {
  return (
    (event.messageType === 'CombatDamageEvent' && event.simultaneous === true) ||
    (event.messageType === 'CombatDeathEvent' && event.simultaneous === true)
  );
}

/**
 * Total time (ms) the frontend needs to animate a full combat stream.
 * Mirrors CombatAnimationView._processCombatStream: the playback starts
 * COMBAT_STREAM_START_DELAY_MS after the stream arrives (the combat-board
 * intro beat before the first attack — once per combat, not between
 * events), the first event of a simultaneous group advances the timeline
 * by its delay, but subsequent simultaneous events in the same group fire
 * at the same instant (no extra delay), so the server's post-combat wait
 * stays in sync with the animation.
 */
export function calculateCombatStreamDurationMs(events: CombatStreamEvent[]): number {
  let total = 0;
  let prevSimultaneous = false;
  for (const e of events) {
    const isSim = isSimultaneousStreamEvent(e);
    if (!(isSim && prevSimultaneous)) {
      total += getCombatEventDelayMs(e);
    }
    prevSimultaneous = isSim;
  }
  // The intro beat only exists when there is something to play after it.
  return events.length > 0 ? total + COMBAT_STREAM_START_DELAY_MS : total;
}

/**
 * How long the server should wait after sending a combat stream before
 * broadcasting the post-combat state (next round / game over):
 * full animation duration + result overlay display + safety buffer,
 * floored at MIN_POST_COMBAT_DELAY_MS.
 *
 * @param streamDurationMs Total animation time of the (longest) stream sent
 *                         (see calculateCombatStreamDurationMs).
 */
export function calculatePostCombatDelayMs(streamDurationMs: number): number {
  const total = streamDurationMs + COMBAT_RESULT_DISPLAY_MS + POST_COMBAT_BUFFER_MS;
  return Math.max(MIN_POST_COMBAT_DELAY_MS, total);
}

/**
 * The delay (ms, relative to the moment the streams were sent) at which a
 * player should receive their "waiting for other combats" RoundTransition
 * message — or null when the player has no perceivable wait.
 *
 * The player's own stream has fully played at `streamDurationMs`, and the
 * client auto-hides the result overlay COMBAT_RESULT_DISPLAY_MS later. If
 * the round only advances within MIN_ROUND_TRANSITION_WAIT_MS of that
 * moment (the player's stream was the longest, or everyone watched the
 * same single combat), the pause is not worth overlaying and the message
 * is suppressed.
 *
 * @param streamDurationMs Total animation time of THIS player's stream
 *                         (see calculateCombatStreamDurationMs).
 * @param postCombatDelayMs When the round advances for everyone
 *                          (see calculatePostCombatDelayMs).
 */
export function calculateRoundTransitionDelayMs(
  streamDurationMs: number,
  postCombatDelayMs: number,
): number | null {
  const waitStartMs = streamDurationMs + COMBAT_RESULT_DISPLAY_MS;
  if (postCombatDelayMs - waitStartMs < MIN_ROUND_TRANSITION_WAIT_MS) return null;
  return waitStartMs;
}
